import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  organization_id: string | null;
}

interface Organization {
  created_by: string;
}

export const useOrganizationAdmin = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOrganizationOwner, setIsOrganizationOwner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsOrganizationOwner(false);
        setLoading(false);
        return;
      }

      try {
        // Get user profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          setLoading(false);
          return;
        }

        // Check if user has admin role using the new user_roles table
        const { data: roles, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id);

        if (!rolesError && roles) {
          const hasAdminRole = roles.some(r => r.role === 'admin');
          setIsAdmin(hasAdminRole);
        }

        // If user has an organization, check if they are the owner
        if (profile?.organization_id) {
          const { data: organization, error: orgError } = await supabase
            .from('organizations')
            .select('created_by')
            .eq('id', profile.organization_id)
            .single();

          if (!orgError && organization) {
            setIsOrganizationOwner(organization.created_by === user.id);
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  // User can edit organization if they are admin OR organization owner
  const canEditOrganization = isAdmin || isOrganizationOwner;

  return {
    isAdmin,
    isOrganizationOwner,
    canEditOrganization,
    loading
  };
};
